// Bring-a-Friend referral vocabulary shared by the admin UI, the registration
// form and the regression tests.
//
// The formula here MUST stay identical to
// `supabase/functions/_shared/referral.ts` — a parity test enforces it.
// Browser math is display-only: checkout always recomputes the authoritative
// discount server-side before charging.

export const REFERRAL_DEFAULT_PERCENT = 50;
export const REFERRAL_DEFAULT_MAX_DISCOUNT_CENTS = 3000;
export const REFERRAL_DEFAULT_MAX_REDEMPTIONS = 1;

/** discount = min(list price × percent, cap). Cents in, cents out. */
export function computeReferralDiscountCents(
  listPriceCents: number,
  discountPercent: number,
  maxDiscountCents: number,
): number {
  if (!Number.isFinite(listPriceCents) || listPriceCents <= 0) return 0;
  const pct = Number.isFinite(discountPercent) ? discountPercent : 0;
  const raw = Math.round((listPriceCents * pct) / 100);
  const capped = Math.min(raw, Math.max(0, Math.round(maxDiscountCents)));
  return Math.max(0, Math.min(capped, listPriceCents));
}

export function normalizeReferralCode(input: unknown): string {
  return String(input ?? "").trim().toUpperCase().replace(/\s+/g, "");
}

/** Friendly code for a child: CHILDFIRSTNAMELASTINITIAL-SSHH. */
export function buildReferralCodeBase(first: string, last?: string | null): string {
  const clean = (v: string) => v.replace(/[^A-Za-z0-9]/g, "").toUpperCase();
  const base = clean(first || "") || "FRIEND";
  return `${base}${clean(last ?? "").slice(0, 1)}-SSHH`;
}

export const centsToDollars = (cents: number) =>
  (Math.round(cents) / 100).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  });

export interface ReferralPromotionRow {
  id: string;
  class_id: string;
  class_slug: string | null;
  class_name: string | null;
  canonical_session_label: string;
  discount_percent: number;
  max_discount_cents: number;
  max_redemptions_per_code: number;
  active: boolean;
  expires_at: string | null;
}

export interface ReferralCodeRow {
  id: string;
  code: string;
  referrer_child_name: string | null;
  referrer_email: string;
  class_id: string;
  canonical_session_label: string;
  redemptions_used: number;
  active: boolean;
  created_at: string;
}

export interface ReferralRedemptionRow {
  id: string;
  code_used: string;
  status: "reserved" | "redeemed" | "released";
  class_id: string;
  canonical_session_label: string;
  referred_email: string | null;
  referred_child_name: string | null;
  referred_registration_id: string | null;
  list_price_cents: number;
  discount_cents: number;
  final_amount_cents: number;
  redeemed_at: string | null;
}
